import { Controller } from "@hotwired/stimulus";

/**
 * Controller that handles file downloads with progress indication
 * Manages download state, spinner visibility, and cleanup of resources
 * Provides fallback timeout and error handling for failed downloads
 */
export default class extends Controller {
  // Define Stimulus targets for DOM elements we need to manipulate
  // These must match data-download-target attributes in the HTML
  static targets = ["form", "button", "spinner", "originalText"];
  declare formTarget: HTMLFormElement;
  declare buttonTarget: HTMLButtonElement;
  declare spinnerTarget: HTMLElement;
  declare originalTextTarget: HTMLElement;

  private timeoutId: number | null = null;
  private readonly TIMEOUT_MS = 60000; // 1 minute fallback

  connect() {
    this.cleanup();
  }

  disconnect() {
    this.cleanup();
  }

  /**
   * Initiates the file download process
   * Shows loading state and handles the download lifecycle
   */
  async startDownload(event: Event) {
    event.preventDefault();
    this.showSpinner();
    this.disableButton();
    this.setFallbackTimeout();

    // Build the download URL with form parameters
    const formData = new FormData(this.formTarget);
    const queryString = new URLSearchParams(formData as any).toString();
    const url = `${this.formTarget.action}?${queryString}`;
    const downloadUrl = new URL(url, window.location.origin).toString();

    try {
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Accept: "text/csv,application/json",
        },
        credentials: "same-origin",
      });

      if (!response.ok) throw new Error("Download failed");

      // Create temporary link element to trigger browser download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = this.getFileName(response);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Clean up and restore UI state
      this.downloadComplete(true);
    } catch (error: any) {
      this.downloadComplete(false);
    }
  }

  private showSpinner() {
    this.spinnerTarget.classList.remove("hidden");
    this.originalTextTarget.classList.add("hidden");
  }

  private hideSpinner() {
    this.spinnerTarget.classList.add("hidden");
    this.originalTextTarget.classList.remove("hidden");
  }

  // UI state management methods
  private disableButton() {
    this.buttonTarget.disabled = true;
    this.buttonTarget.classList.add("opacity-50", "cursor-not-allowed");
  }

  private enableButton() {
    this.buttonTarget.disabled = false;
    this.buttonTarget.classList.remove("opacity-50", "cursor-not-allowed");
  }

  // Extracts filename from Content-Disposition header or uses default
  private getFileName(response: Response): string {
    const disposition = response.headers.get("content-disposition");
    if (disposition && disposition.includes("filename=")) {
      return disposition.split("filename=")[1].replace(/['"]/g, "");
    }
    // Fallback filename if none provided in headers
    return "download.csv";
  }

  private downloadComplete(success: boolean = true) {
    this.cleanup();
    this.hideSpinner();
    this.enableButton();
    if (!success && !this.element.classList.contains("no-alert")) {
      alert("Download failed. Please try again.");
    }
  }

  // Sets a timeout to prevent UI from being stuck in loading state
  private setFallbackTimeout() {
    this.timeoutId = window.setTimeout(() => {
      this.downloadComplete();
    }, this.TIMEOUT_MS);
  }

  // Cleanup method to prevent memory leaks
  private cleanup() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }
}
